// vessel status — the warehouse book (ΚΑΤΑΣΤΑΣΗ ΣΚΑΦΩΝ ΑΠΟΘΗΚΗ) as a D1-backed editable grid.
// User-inputed data. recreates the Excel look: per-cell fill colours (preserved on import and
// user-editable via the palette), a sticky header + first column, and native date pickers in
// date columns. Import/export keep it in sync with the .xlsx.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSaveVesselStatus, useVesselStatus, type VesselStatusSheet } from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { ActionBar, EmptyState, ErrorState, InlineError, LoadingState, OverflowActions, PageHeader, useConfirm, type OverflowAction } from '../components/ui.js';
import { IconDownload, IconRefresh, IconUpload } from '../components/icons.js';
import { useDateFormatter } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';

// ── date/serial helpers ──────────────────────────────────────────────────────────
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const pad2 = (n: number) => String(n).padStart(2, '0');
const isISO = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
const isSerial = (s: string) => /^\d{4,5}(\.\d+)?$/.test(s.trim()) && +s >= 20000 && +s < 80000;
const serialToISO = (n: number) => {
  const d = new Date(EXCEL_EPOCH + Math.round(n) * 86400000);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};
const toISO = (s: string) => (isISO(s) ? s : isSerial(s) ? serialToISO(+s) : '');
const isDateVal = (s: string) => isISO(s) || isSerial(s);
const isOk = (s: string) => { const t = s.trim().toLowerCase(); return t === 'ok' || t === 'οκ'; };

// A column is a date column if most of its meaningful cells are dates.
function inferDateCols(rows: string[][]): number[] {
  const cols = Math.max(0, ...rows.map((r) => r.length));
  const out: number[] = [];
  for (let c = 0; c < cols; c++) {
    let cand = 0, dates = 0;
    for (let r = 1; r < rows.length; r++) {
      const v = (rows[r]?.[c] ?? '').trim();
      if (!v || v === ':' || isOk(v)) continue;
      cand++;
      if (isDateVal(v)) dates++;
    }
    if (cand >= 2 && dates >= cand * 0.5) out.push(c);
  }
  return out;
}

// [fill, meaning, the short caption the phone palette writes under the swatch]. The hex is what is STORED
// (and exported), exactly as the warehouse Excel has it; on screen each fill is drawn in the app's status
// vocabulary instead (fillKind → a .vs-f-* class), so the grid reads in light, dark and every profile.
const SWATCHES: Array<[string, string, string]> = [
  ['92D050', 'Done / received', 'Done'],
  ['C00000', 'Missing / urgent', 'Missing'],
  ['FFFF00', 'Attention', 'Attention'],
  ['9DC3E6', 'Note', 'Note'],
  ['F8CBAD', 'Engine / header', 'Engine'],
  ['DBDBDB', 'Group', 'Group'],
];

// how a stored fill renders: Done ok green, Missing err red, Attention warn amber, Note info blue, Engine a
// peach drawn from the warn / err tokens, Group a neutral band; a HOLD cell is the amber hold hatch.
type FillKind = 'ok' | 'err' | 'warn' | 'info' | 'eng' | 'grp' | 'hold';
const PALETTE_KIND: Record<string, FillKind> = {
  '92D050': 'ok', C00000: 'err', FFFF00: 'warn', '9DC3E6': 'info', F8CBAD: 'eng', DBDBDB: 'grp',
};
/** the palette's own six map one to one; any other imported Excel fill goes to the nearest meaning by hue
 *  (greys to Group, reds and pinks to Missing, pale oranges to Engine, oranges and yellows to Attention,
 *  greens to Done, blues and purples to Note). */
function fillKind(hex: string): FillKind | null {
  const h = hex.replace('#', '').toUpperCase();
  if (!h) return null;
  const known = PALETTE_KIND[h];
  if (known) return known;
  if (!/^[0-9A-F]{6}$/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255, g = parseInt(h.slice(2, 4), 16) / 255, b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (d < 0.12) return 'grp';
  let hue = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  hue = (hue * 60 + 360) % 360;
  if (hue < 12 || hue >= 300) return 'err';
  if (hue < 38) return (max + min) / 2 > 0.7 ? 'eng' : 'warn';
  if (hue < 70) return 'warn';
  if (hue < 165) return 'ok';
  return 'info';
}
const isHold = (s: string) => /^(on\s+)?hold$/i.test(s.trim());
const fillCls = (kind: FillKind | null) => (kind ? ` vs-f-${kind}` : '');

interface Sheet { name: string; rows: string[][]; fills: string[][]; dateCols: number[] }

function normalize(sheets: VesselStatusSheet[]): Sheet[] {
  return sheets.map((s) => {
    const cols = Math.max(1, ...s.rows.map((r) => r.length));
    const rows = s.rows.map((r) => Array.from({ length: cols }, (_, i) => String(r[i] ?? '')));
    const fills = rows.map((_, ri) => Array.from({ length: cols }, (_, ci) => String(s.fills?.[ri]?.[ci] ?? '')));
    const stored = new Set(s.dateCols ?? []);
    for (const c of inferDateCols(rows)) stored.add(c);
    return { name: s.name, rows, fills, dateCols: [...stored].sort((a, b) => a - b) };
  });
}

export function VesselStatus() {
  const { can } = useAuth();
  const canWrite = can('vesselstatus:write');
  const df = useDateFormatter();
  const { data, isLoading, isFetching, error, refetch } = useVesselStatus();
  const save = useSaveVesselStatus();
  const fileInput = useRef<HTMLInputElement>(null);

  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [active, setActive] = useState(0);
  const [sel, setSel] = useState<{ r: number; c: number } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [importing, setImporting] = useState(false);
  const [ioErr, setIoErr] = useState<unknown>(null);
  const [confirmNode, confirm] = useConfirm();
  const isPhone = useIsPhone();

  useEffect(() => {
    if (data) { setSheets(normalize(data.sheets)); setActive(0); setSel(null); setDirty(false); }
  }, [data]);

  const sheet = sheets[active];
  const cols = useMemo(() => (sheet ? Math.max(1, ...sheet.rows.map((r) => r.length)) : 0), [sheet]);
  const dateSet = useMemo(() => new Set(sheet?.dateCols ?? []), [sheet]);

  const mutate = (fn: (s: Sheet) => Sheet) => {
    setSheets((prev) => prev.map((s, i) => (i === active ? fn(s) : s)));
    setDirty(true);
  };
  const commit = (r: number, c: number, val: string) =>
    mutate((s) => ({ ...s, rows: s.rows.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? val : cell)) : row)) }));
  const setFill = (r: number, c: number, hex: string) =>
    mutate((s) => ({ ...s, fills: s.fills.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? hex : cell)) : row)) }));

  const applySwatch = (hex: string) => { if (sel) setFill(sel.r, sel.c, hex); };
  const applyMark = (val: string) => { if (sel) commit(sel.r, sel.c, val); };

  const addRow = () => mutate((s) => ({
    ...s,
    rows: [...s.rows, Array.from({ length: cols }, () => '')],
    fills: [...s.fills, Array.from({ length: cols }, () => '')],
  }));

  const importXlsx = async (file: File) => {
    const XLSX: any = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellStyles: true, cellNF: true, cellDates: false });
    const imported: VesselStatusSheet[] = wb.SheetNames.map((name: string) => {
      const ws = wb.Sheets[name];
      if (!ws || !ws['!ref']) return { name, rows: [], fills: [], dateCols: [] };
      const ref = XLSX.utils.decode_range(ws['!ref']);
      const rows: string[][] = [], fills: string[][] = [];
      const dateCols = new Set<number>();
      for (let r = 0; r <= ref.e.r; r++) {
        const row: string[] = [], frow: string[] = [];
        for (let c = 0; c <= ref.e.c; c++) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          let val = '', fill = '';
          if (cell) {
            const nf: string = typeof cell.z === 'string' ? cell.z : '';
            const dateFmt = /[dmyDMY]/.test(nf) && !/[h]/i.test(nf);
            if (cell.t === 'n' && dateFmt) { val = serialToISO(cell.v); dateCols.add(c); }
            else if (cell.t === 'n') val = String(cell.v);
            else val = String(cell.v ?? '');
            const fg = cell.s && cell.s.fgColor;
            if (fg && fg.rgb && cell.s.patternType && cell.s.patternType !== 'none') {
              const hex = String(fg.rgb).slice(-6).toUpperCase();
              if (hex !== 'FFFFFF' && hex !== '000000') fill = hex;
            }
          }
          row.push(val); frow.push(fill);
        }
        rows.push(row); fills.push(frow);
      }
      return { name, rows, fills, dateCols: [...dateCols].sort((a, b) => a - b) };
    });
    setSheets(normalize(imported)); setActive(0); setSel(null); setDirty(true);
  };

  const exportXlsx = async () => {
    const XLSX: any = await import('xlsx');
    const wb = XLSX.utils.book_new();
    for (const s of sheets) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s.rows), (s.name || 'Sheet').slice(0, 31));
    XLSX.writeFile(wb, 'vessel-status.xlsx');
  };

  const doSave = () =>
    save.mutate({ sheets: sheets.map((s) => ({ name: s.name, rows: s.rows, fills: s.fills, dateCols: s.dateCols })) },
      { onSuccess: () => setDirty(false) });

  // Import / Export / Refresh: inline on wider screens, one ⋯ sheet on phones ('+ Row' stays visible)
  const importAction: OverflowAction | false = canWrite && {
    label: importing ? 'Importing…' : 'Import', icon: importing ? undefined : <IconUpload size={14} />, disabled: importing,
    onClick: () => fileInput.current?.click(),
  };
  const exportAction: OverflowAction = {
    label: 'Export', icon: <IconDownload size={14} />, disabled: sheets.length === 0,
    onClick: () => { setIoErr(null); exportXlsx().catch(setIoErr); },
  };
  const refreshAction: OverflowAction = {
    label: isFetching ? 'Refreshing…' : 'Refresh', icon: isPhone ? <IconRefresh size={14} /> : undefined, disabled: isFetching,
    onClick: async () => {
      // a changed server copy resets the grid, so unsaved edits would be lost without a word
      if (dirty && !(await confirm({ title: 'Discard unsaved changes?', message: 'Refreshing can replace edits you have not saved.', confirmLabel: 'Refresh', danger: true }))) return;
      void refetch();
    },
  };
  const saveLabel = save.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved';

  return (
    <>
      <PageHeader title="Vessel Status">
        {data?.importedBy && <span className="muted">saved {df.dateTime(data.importedAt)} · {data.importedBy}</span>}
        {canWrite && (
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) { setIoErr(null); setImporting(true); importXlsx(f).catch(setIoErr).finally(() => setImporting(false)); }
              e.target.value = '';
            }}
          />
        )}
        <OverflowActions items={isPhone ? [importAction, exportAction, refreshAction] : [importAction, exportAction]} title="Vessel Status" />
        {canWrite && <button className="btn btn-sm" disabled={!sheet} onClick={addRow}>+ Row</button>}
        {/* phones save from the bottom bar, beside the palette */}
        {canWrite && !isPhone && (
          <button className="btn btn-sm btn-primary" disabled={!dirty || save.isPending} onClick={doSave}>{saveLabel}</button>
        )}
        {!isPhone && <OverflowActions items={[refreshAction]} />}
      </PageHeader>


      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
      {ioErr != null && <InlineError error={ioErr} onDismiss={() => setIoErr(null)} />}

      {data && sheets.length === 0 && (
        <EmptyState title="No data yet" hint={canWrite ? 'Import the warehouse Excel to get started.' : undefined} />
      )}

      {sheets.length > 0 && (
        <>
          <div className="toolbar">
            {sheets.map((s, i) => (
              <button key={i} type="button" aria-pressed={i === active} className={`chip ${i === active ? 'active' : ''}`} onClick={() => { setActive(i); setSel(null); }}>{s.name || `Sheet ${i + 1}`}</button>
            ))}
          </div>

          {canWrite && !isPhone && (
            <div className="vs-pal">
              <span className="vs-pal-lbl">Cell colour</span>
              {SWATCHES.map(([hex, title]) => (
                <button key={hex} className={`vs-sw${fillCls(fillKind(hex))}`} title={title} aria-label={title} disabled={!sel} onClick={() => applySwatch(hex)} />
              ))}
              <button className="vs-sw none" title="No fill" disabled={!sel} onClick={() => applySwatch('')} />
              <span className="vs-pal-lbl" style={{ marginLeft: 8 }}>Mark</span>
              <button className="vs-mk" disabled={!sel} onClick={() => applyMark(':')}>∶ N/A</button>
              <button className="vs-mk" disabled={!sel} onClick={() => applyMark('Ok')}>Ok</button>
              <button className="vs-mk" disabled={!sel} onClick={() => applyMark('')}>Clear</button>
              {sel && <span className="muted" style={{ marginLeft: 6 }}>R{sel.r + 1}·C{sel.c + 1}</span>}
            </div>
          )}

          {sheet && (
            <div className="vs-wrap">
              <table className="vs-grid">
                <tbody>
                  {sheet.rows.map((row, r) => (
                    <tr key={r}>
                      {Array.from({ length: cols }, (_, c) => {
                        const val = row[c] ?? '';
                        const fill = sheet.fills[r]?.[c] ?? '';
                        const isHd = r === 0;
                        const isFirst = c === 0;
                        const isDateCol = dateSet.has(c);
                        const activeCls = sel && sel.r === r && sel.c === c ? ' vs-active' : '';
                        // the stored fill wins (incl. the engine headers), drawn as its status class; a HOLD cell
                        // is the hold hatch whatever its fill; a header without one keeps the .vs-hd band.
                        const kind = !isHd && isHold(val) ? 'hold' : fillKind(fill);
                        const tdCls = `${isHd ? 'vs-hd' : ''}${isFirst ? ' vs-firstcol' : ''}${fillCls(kind)}${activeCls}`.trim();
                        const onPick = () => canWrite && setSel({ r, c });

                        // date cell: empty or a date value → native picker for editors, the date as text in
                        // the user's format for everyone else; markers/other → text.
                        if (!isHd && isDateCol && (val === '' || isDateVal(val)) && !canWrite) {
                          return <td key={c} className={tdCls}>{df.date(toISO(val))}</td>;
                        }
                        if (!isHd && isDateCol && (val === '' || isDateVal(val))) {
                          return (
                            <td key={c} className={tdCls} onMouseDown={onPick}>
                              <input
                                className="vs-date"
                                type="date"
                                value={toISO(val)}
                                disabled={!canWrite}
                                onFocus={onPick}
                                onChange={(e) => commit(r, c, e.target.value)}
                              />
                            </td>
                          );
                        }
                        const cellCls = `vs-cell${!isHd && val.trim() === ':' ? ' vs-marker' : ''}${!isHd && isOk(val) ? ' vs-ok' : ''}`;
                        return (
                          <td key={c} className={tdCls} onMouseDown={onPick}>
                            <span
                              className={cellCls}
                              contentEditable={canWrite}
                              suppressContentEditableWarning
                              onFocus={onPick}
                              onBlur={(e) => { const v = e.currentTarget.textContent ?? ''; if (v !== val) commit(r, c, v); }}
                            >
                              {val}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {confirmNode}
      {/* phones: the palette and Save ride at the bottom, in reach of whichever cell is picked lower down */}
      <ActionBar
        show={canWrite && !!sheet}
        className="vs-bar"
        message={sel ? undefined : dirty ? 'Unsaved changes · tap a cell to colour or mark it' : 'Tap a cell to colour or mark it'}
      >
        {sel ? (
          <>
            <div className="vs-bar-sw" role="group" aria-label="Cell colour">
              {SWATCHES.map(([hex, title, short]) => (
                <button key={hex} type="button" className="vs-swb" title={title} aria-label={title} onClick={() => applySwatch(hex)}>
                  <span className={`vs-sw${fillCls(fillKind(hex))}`} />
                  <span className="vs-swb-lb" aria-hidden="true">{short}</span>
                </button>
              ))}
              <button type="button" className="vs-swb" title="No fill" aria-label="No fill" onClick={() => applySwatch('')}>
                <span className="vs-sw none" />
                <span className="vs-swb-lb" aria-hidden="true">None</span>
              </button>
            </div>
            <div className="vs-bar-row">
              <button className="btn btn-sm" onClick={() => applyMark(':')}>∶ N/A</button>
              <button className="btn btn-sm" onClick={() => applyMark('Ok')}>Ok</button>
              <button className="btn btn-sm" onClick={() => applyMark('')}>Clear</button>
              <span className="vs-bar-at">R{sel.r + 1}·C{sel.c + 1}</span>
              <button className="btn btn-sm btn-ghost" onClick={() => setSel(null)}>Done</button>
              {dirty && <button className="btn btn-sm btn-primary" disabled={save.isPending} onClick={doSave}>{saveLabel}</button>}
            </div>
          </>
        ) : (
          dirty && <button className="btn btn-primary" disabled={save.isPending} onClick={doSave}>{saveLabel}</button>
        )}
      </ActionBar>
    </>
  );
}
